import { z } from "zod";
import {
  ClusterAutoTuneNotificationConfigSchema,
  DEFAULT_CLUSTER_AUTO_TUNE_NOTIFICATION_CONFIG,
} from "@/pages/clusters/auto-tune/types";
import type { ClusterAutoTuneNotificationConfig } from "@/pages/clusters/auto-tune/types";

// Backoff + notification config for jobs — kept in its own module rather
// than ``./types.ts`` (which ``@/pages/clusters/auto-tune/types`` already
// imports the status/history/savings schemas FROM) so this file's own
// reverse import of the cluster module's backoff/notification schemas
// never becomes a circular dependency: at module-eval time, whichever
// of the two files loads first would otherwise see the other's
// not-yet-defined exports as ``undefined`` and crash on
// ``.catch()``/spread.

// Latency as a percent of the job's pre-tune baseline run time: 100 is
// the baseline itself (any regression backs off), 1000 only a 10x blowup.
export const MIN_JOB_LATENCY_THRESHOLD_PCT = 100;
export const MAX_JOB_LATENCY_THRESHOLD_PCT = 1000;
export const DEFAULT_JOB_LATENCY_THRESHOLD_PCT = 150;

// Job backoff policy: the one setting a job has. Unlike
// clusters/warehouses (a cold-start rate), the job signal is run latency
// against the pre-tune baseline. The cluster policy's churn / cold-start
// counters and its trigger action are not part of the job shape — each
// describes behaviour the cluster and warehouse backoff monitors
// implement, nothing on the job side evaluates them, and this drawer
// renders none of them. ``.catch()`` (not ``.default()``) — an explicit
// ``null`` (a real shape the BE can send) must fall back to the default
// instead of parsing through as the literal string "undefined" wherever
// the drawer displays it.
export const JobAutoTuneBackoffPolicySchema = z.object({
  latency_threshold_pct: z.number().catch(DEFAULT_JOB_LATENCY_THRESHOLD_PCT),
});
export type JobAutoTuneBackoffPolicy = z.infer<typeof JobAutoTuneBackoffPolicySchema>;

export const DEFAULT_JOB_AUTO_TUNE_BACKOFF_POLICY: JobAutoTuneBackoffPolicy = {
  latency_threshold_pct: DEFAULT_JOB_LATENCY_THRESHOLD_PCT,
};

export const isJobBackoffPolicyValid = (policy: JobAutoTuneBackoffPolicy): boolean =>
  policy.latency_threshold_pct >= MIN_JOB_LATENCY_THRESHOLD_PCT &&
  policy.latency_threshold_pct <= MAX_JOB_LATENCY_THRESHOLD_PCT;

// GET /jobs/{job_id}/auto-tune/config response — same wire shape as the
// cluster config endpoint's notification half (reused directly), with
// the job-specific backoff policy above.
export const JobAutoTuneConfigResponseSchema = z.object({
  backoff_policy: JobAutoTuneBackoffPolicySchema.catch(DEFAULT_JOB_AUTO_TUNE_BACKOFF_POLICY),
  notification_config: ClusterAutoTuneNotificationConfigSchema.catch(
    DEFAULT_CLUSTER_AUTO_TUNE_NOTIFICATION_CONFIG
  ),
  entity_type: z.string().nullable().optional(),
});
export type JobAutoTuneConfigResponse = z.infer<typeof JobAutoTuneConfigResponseSchema>;

// PATCH body — same shape minus ``entity_type`` (the API infers it from
// the route).
export interface JobAutoTuneConfigRequest {
  backoff_policy: JobAutoTuneBackoffPolicy;
  notification_config: ClusterAutoTuneNotificationConfig;
}
